import {
  UseQueryOptions,
  useMutation,
  useQueries,
  useQuery
} from '@tanstack/react-query';
import { HTTPError } from 'ky';

import { queryClient } from '@/adapters/query';
import { crmScreenerApiClient } from '@/adapters/schemaClients';
import {
  GetOneFacilitySetting200Response,
  GetScreenerSetting200Response
} from '@/types/crm-screener-api';
import {
  GetOneFacilitySettingRequest,
  GetScreenerSettingRequest,
  UpdateScreenerSettingOperationRequest
} from '@/types/crm-screener-api/apis/DefaultApi';

const FIND_ONE_SCREENER_SETTING_QUERY_KEY = 'findOneScreenerSetting';

export enum ScreenerSettingName {
  LostReferralCleanUp = 'lost_referral_clean_up',
  RuleAndDataPullCategories = 'rule_and_data_pull_categories',
  BedBoardEnabled = 'bed_board',
  OverrideAiAnalysisResult = 'override_ai_analysis_result',
  EfaxInboxEnabled = 'efax_inbox',
  SkipReferralCreationForSpamFax = 'skip_referral_creation_for_spam_fax',
  SkipReferralCreationForNonReferralFax = 'skip_referral_creation_for_non_referral_fax',
  DisableExacareFrontend = 'disable_exacare_frontend',
  PushConditionsToPcc = 'push_conditions_to_pcc'
}

export const invalidateScreenerSettingsQueries = () => {
  queryClient.invalidateQueries([FIND_ONE_SCREENER_SETTING_QUERY_KEY]);
  queryClient.invalidateQueries(['screenerFacilitySettings']);
};

export const useScreenerSettingFindOne = (
  params: GetScreenerSettingRequest,
  options: UseQueryOptions<GetScreenerSetting200Response, HTTPError> = {}
) =>
  useQuery<GetScreenerSetting200Response, HTTPError>(
    [FIND_ONE_SCREENER_SETTING_QUERY_KEY, params],
    () => crmScreenerApiClient.getScreenerSetting(params),
    {
      enabled: !!params.settingName,
      ...options
    }
  );

export const useScreenerSettingFindOneByFacilityId = (
  params: GetOneFacilitySettingRequest,
  options: UseQueryOptions<GetOneFacilitySetting200Response, HTTPError> = {}
) =>
  useQuery<GetOneFacilitySetting200Response, HTTPError>(
    [FIND_ONE_SCREENER_SETTING_QUERY_KEY, params],
    () => crmScreenerApiClient.getOneFacilitySetting(params),
    {
      enabled: !!params.settingName && !!params.facilityId,
      ...options
    }
  );

export const useScreenerSettingsFindAll = (
  settingNames: ScreenerSettingName[]
) => {
  const queries = useQueries({
    queries: settingNames.map((settingName) => ({
      queryKey: [FIND_ONE_SCREENER_SETTING_QUERY_KEY, { settingName }],
      queryFn: () => crmScreenerApiClient.getScreenerSetting({ settingName }),
      enabled: !!settingName
    }))
  });

  const isLoading = queries.some((q) => q.isLoading);
  const isError = queries.some((q) => q.isError);

  const settingsMap = settingNames.reduce(
    (acc, settingName, index) => {
      acc[settingName] = queries[index].data?.data?.value;
      return acc;
    },
    {} as Record<ScreenerSettingName, string | undefined>
  );

  return {
    queries,
    isLoading,
    isError,
    settingsMap
  };
};

export const useScreenerSettingsQuery = () => {
  return {
    invalidateScreenerSettingsQueries,
    useFindOne: useScreenerSettingFindOne,
    useFindOneByFacilityId: useScreenerSettingFindOneByFacilityId,
    useFindAll: useScreenerSettingsFindAll,
    mutations: {
      update: useMutation(
        (params: UpdateScreenerSettingOperationRequest) =>
          crmScreenerApiClient.updateScreenerSetting(params),
        {
          onSuccess: invalidateScreenerSettingsQueries
        }
      )
    }
  };
};
